import type { CSSProperties } from "react";

export type OrbState = "planning" | "developing" | "ci" | "approval" | "error";

/** color = token de tema; speed = segundos por vuelta (0 = sin giro). approval respira en vez de girar. */
const ORB: Record<OrbState, { color: string; speed: number; breathe?: boolean }> = {
  planning: { color: "ac", speed: 7 },
  developing: { color: "gd", speed: 3.5 },
  ci: { color: "in", speed: 5 },
  approval: { color: "wn", speed: 9, breathe: true },
  error: { color: "er", speed: 0 },
};

/** Color de tema y velocidad del estado, para pintar elementos que acompañan al orbe (barrido, etapa actual). */
export const orbColor = (state: OrbState) => `var(--${ORB[state].color})`;
export const orbSpeed = (state: OrbState) => ORB[state].speed;

const abs: CSSProperties = { position: "absolute", inset: 0 };

/** Orbe del agente: sustituye al punto pulsante. Decorativo; la etiqueta de estado siempre va al lado. */
export function ThinkingOrb({ state, size = 14 }: { state: OrbState; size?: number }) {
  const { color, speed, breathe } = ORB[state];
  const c = `var(--${color})`;
  const spin = (name: string, s: number): CSSProperties => ({ ...abs, animation: breathe || !speed ? "none" : `${name} ${s}s linear infinite` });
  const blob = (style: CSSProperties): CSSProperties => ({ position: "absolute", borderRadius: "50%", filter: `blur(${Math.max(1, size * 0.09)}px)`, ...style });
  return (
    <span aria-hidden style={{ position: "relative", display: "inline-block", flex: "none", width: size, height: size, borderRadius: "50%", overflow: "hidden", background: `color-mix(in srgb, ${c} 20%, var(--bg))`, animation: breathe ? "orb-breathe 4s ease-in-out infinite" : "none" }}>
      <span style={spin("orb-spin", speed)}>
        <span style={blob({ left: "6%", top: "10%", width: "62%", height: "62%", background: `radial-gradient(circle, ${c}, transparent 70%)` })} />
        <span style={blob({ right: "4%", bottom: "8%", width: "50%", height: "50%", background: `radial-gradient(circle, color-mix(in srgb, ${c} 55%, #fff), transparent 70%)`, opacity: 0.8 })} />
      </span>
      <span style={spin("orb-spin-r", speed * 1.6)}>
        <span style={blob({ left: "38%", top: "42%", width: "48%", height: "48%", background: `radial-gradient(circle, color-mix(in srgb, ${c} 70%, var(--bg)), transparent 70%)`, opacity: 0.9 })} />
      </span>
      <span style={{ ...abs, background: "radial-gradient(circle at 30% 24%, rgba(255,255,255,.38), transparent 42%)" }} />
    </span>
  );
}
